import { Component } from 'react';
import { ConverterLayout, layoutStyles } from '../converter-layout/converter-layout';
import { CodeBlock } from '../converter-layout/code-block';
import { isSubsrtFormat, parseCustom, emitCustom, cuesToSubsrt, subsrtToCues, fixSsaColours, type SubtitleFormat, type Cue } from './parsers';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; subsrt: any }
    | { kind: 'error'; message: string };

const FORMATS: SubtitleFormat[] = ['srt', 'vtt', 'ass', 'ssa', 'sbv', 'sub', 'lrc', 'smi', 'ttml', 'stl', 'dfxp', 'txt', 'csv'];

const SAMPLE_SRT = `1
00:00:01,000 --> 00:00:04,000
The quick brown fox

2
00:00:04,000 --> 00:00:07,000
jumps over the lazy dog.

3
00:00:07,000 --> 00:00:10,000
Subtitle converter demo.
`;

interface State {
    fromFormat: SubtitleFormat;
    toFormat: SubtitleFormat;
    input: string;
    output: string;
    error: string | null;
    lib: LibState;
    copied: boolean;
}

function parseSlug(slug: string | undefined): { from: SubtitleFormat; to: SubtitleFormat } | null {
    if (!slug) return null;
    // Format hub ({ext}-converter): from = the hub's format, to = a sensible default.
    const hub = slug.match(/^([a-z0-9]+)-converter$/);
    if (hub) {
        const from = hub[1] as SubtitleFormat;
        if (!FORMATS.includes(from)) return null;
        return { from, to: (from === 'vtt' ? 'srt' : 'vtt') as SubtitleFormat };
    }
    const m = slug.match(/^convert-([a-z0-9]+)-to-([a-z0-9]+)$/);
    if (!m) return null;
    if (!FORMATS.includes(m[1] as SubtitleFormat) || !FORMATS.includes(m[2] as SubtitleFormat)) return null;
    return { from: m[1] as SubtitleFormat, to: m[2] as SubtitleFormat };
}

export default class SubtitleConverterComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const parsed = parseSlug(props.feature) || { from: 'srt', to: 'vtt' };
        this.state = {
            fromFormat: parsed.from,
            toFormat: parsed.to,
            input: parsed.from === 'srt' ? SAMPLE_SRT : '',
            output: '',
            error: null,
            lib: { kind: 'idle' },
            copied: false,
        };
    }

    componentDidMount() {
        if (this.state.input) void this.convert();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureSubsrt(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.subsrt;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('subsrt-ts');
            const subsrt = (mod as any).default || mod;
            this.setState({ lib: { kind: 'ready', subsrt } });
            return subsrt;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load subsrt-ts' } });
            return null;
        }
    }

    private async parse(format: SubtitleFormat, text: string): Promise<Cue[]> {
        if (isSubsrtFormat(format)) {
            const subsrt = await this.ensureSubsrt();
            if (!subsrt) return [];
            const captions = subsrt.parse(text, { format });
            return subsrtToCues(captions);
        }
        return parseCustom(format, text);
    }

    private async emit(format: SubtitleFormat, cues: Cue[]): Promise<string> {
        if (format === 'ass' || format === 'ssa') {
            const subsrt = await this.ensureSubsrt();
            if (!subsrt) return '';
            const out = subsrt.build(cuesToSubsrt(cues), { format });
            return format === 'ssa' ? fixSsaColours(out) : out;
        }
        if (isSubsrtFormat(format)) {
            const subsrt = await this.ensureSubsrt();
            if (!subsrt) return '';
            return subsrt.build(cuesToSubsrt(cues), { format });
        }
        return emitCustom(format, cues);
    }

    convert = async () => {
        const { fromFormat, toFormat, input } = this.state;
        if (!input.trim()) {
            this.setState({ output: '', error: null });
            return;
        }
        try {
            const cues = await this.parse(fromFormat, input);
            if (cues.length === 0) {
                this.setState({ error: 'No cues parsed from input. Check the format.', output: '' });
                return;
            }
            const result = await this.emit(toFormat, cues);
            this.setState({ output: result, error: null });
        } catch (e: any) {
            this.setState({ error: e.message || 'Conversion failed', output: '' });
        }
    };

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            this.setState({ input: String(reader.result || '') }, () => void this.convert());
        };
        reader.readAsText(f);
    };

    download = () => {
        if (!this.state.output) return;
        const blob = new Blob([this.state.output], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `subtitles.${this.state.toFormat}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { fromFormat, toFormat, input, output, error, lib, copied } = this.state;
        const acceptList = ".srt,.vtt,.ass,.ssa,.sbv,.sub,.lrc,.smi,.ttml,.stl,.dfxp,.txt,.csv,text/*";

        return (
            <ConverterLayout
                toolbar={<>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="from-format">From format</label>
                        <select id="from-format" value={fromFormat}
                            onChange={e => this.setState({ fromFormat: e.target.value as SubtitleFormat }, () => void this.convert())}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="to-format">To format</label>
                        <select id="to-format" value={toFormat}
                            onChange={e => this.setState({ toFormat: e.target.value as SubtitleFormat }, () => void this.convert())}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                </>}

                inputLabel={`Input (${fromFormat.toUpperCase()})`}
                inputMeta={input ? `${input.length} chars` : undefined}
                inputArea={
                    <textarea className={layoutStyles.area} value={input}
                        placeholder={`Paste ${fromFormat.toUpperCase()} content here…`}
                        spellCheck={false}
                        onChange={e => this.setState({ input: e.target.value })} />
                }
                inputActions={
                    <label className={layoutStyles.fileDrop}>
                        📂 Upload .{fromFormat}
                        <input type="file" accept={acceptList} onChange={this.onFileChange} />
                    </label>
                }

                outputLabel={`Output (${toFormat.toUpperCase()})`}
                outputMeta={output ? `${output.length} chars` : undefined}
                outputArea={output ? (
                    <div style={{ minHeight: 320 }}>
                        <CodeBlock code={output}
                            language={toFormat === 'ttml' || toFormat === 'dfxp' || toFormat === 'smi' ? 'xml' : toFormat === 'csv' ? null : null}
                            wrapLines maxHeight={500} ariaLabel="Converted subtitle output" />
                    </div>
                ) : (
                    <textarea className={layoutStyles.area} value="" readOnly
                        placeholder="Converted output will appear here…" />
                )}
                outputActions={<>
                    <button className={`${layoutStyles.iconBtn} ${copied ? layoutStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                    <button className={`${layoutStyles.iconBtn} ${layoutStyles.iconBtnPrimary}`}
                        onClick={this.download} disabled={!output}>
                        ⬇ Download .{toFormat}
                    </button>
                </>}

                error={error || (lib.kind === 'error' ? (lib as any).message : null)}

                primaryAction={
                    <button className={layoutStyles.primaryBtn}
                        onClick={this.convert}
                        disabled={!input.trim() || lib.kind === 'loading'}>
                        {lib.kind === 'loading' ? 'Loading subsrt…' : 'Convert →'}
                    </button>
                }
            />
        );
    }
}
